/**
 * Home page banner (blocks/slideshow.liquid + blocks/slide.liquid).
 *
 * Slides sit side by side in a scroll-snap track, so swiping works on
 * its own. This adds arrows, one dot per slide and, when turned on in the
 * theme editor, rotation every few seconds with a pause button.
 * Rotation stops on hover or focus, when the tab is hidden, after someone
 * picks a slide themselves, and never runs with reduced motion.
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  document.querySelectorAll('[data-slideshow]').forEach((show) => {
    if (show.dataset.ready) return;
    show.dataset.ready = 'true';

    const track = show.querySelector('[data-slideshow-track]');
    const slides = [...track.querySelectorAll('[data-slide]')];
    if (slides.length < 2) return;

    const controls = show.querySelector('[data-slideshow-controls]');
    const dotsBox = show.querySelector('[data-slideshow-dots]');
    const pauseBtn = show.querySelector('[data-slideshow-pause]');
    const interval = Math.max(3, Number(show.dataset.interval) || 6) * 1000;
    let at = 0;
    let timer = null;
    let stopped = show.dataset.autoplay !== 'true' || reduceMotion.matches;
    let hovering = false;

    const dots = slides.map((slide, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'slideshow__dot';
      dot.setAttribute(
        'aria-label',
        show.dataset.slideLabel.replace('#', String(i + 1)).replace('%', String(slides.length))
      );
      dot.addEventListener('click', () => {
        stop();
        go(i);
      });
      dotsBox.append(dot);
      return dot;
    });

    const mark = (i) => {
      at = i;
      dots.forEach((d, n) => {
        if (n === i) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
      slides.forEach((s, n) => s.toggleAttribute('inert', n !== i));
    };

    const go = (i) => {
      const next = (i + slides.length) % slides.length;
      track.scrollTo({ left: slides[next].offsetLeft - track.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      mark(next);
    };

    const tick = () => {
      clearTimeout(timer);
      if (stopped || hovering || document.hidden) return;
      timer = setTimeout(() => {
        go(at + 1);
        tick();
      }, interval);
    };

    const syncPause = () => {
      pauseBtn.classList.toggle('is-paused', stopped);
      pauseBtn.setAttribute('aria-label', stopped ? pauseBtn.dataset.labelPlay : pauseBtn.dataset.labelPause);
    };

    const stop = () => {
      stopped = true;
      clearTimeout(timer);
      syncPause();
    };

    controls.hidden = false;
    if (show.dataset.autoplay === 'true' && !reduceMotion.matches) pauseBtn.hidden = false;
    syncPause();
    mark(0);

    show.querySelectorAll('[data-slideshow-step]').forEach((btn) =>
      btn.addEventListener('click', () => {
        stop();
        go(at + Number(btn.dataset.slideshowStep));
      })
    );

    pauseBtn.addEventListener('click', () => {
      stopped = !stopped;
      syncPause();
      tick();
    });

    // Follow swipes: whichever slide is mostly in view is the current one.
    if ('IntersectionObserver' in window) {
      const seen = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) mark(slides.indexOf(e.target));
          });
        },
        { root: track, threshold: 0.6 }
      );
      slides.forEach((s) => seen.observe(s));
    }
    track.addEventListener('pointerdown', stop, { passive: true });

    show.addEventListener('mouseenter', () => {
      hovering = true;
      clearTimeout(timer);
    });
    show.addEventListener('mouseleave', () => {
      hovering = false;
      tick();
    });
    show.addEventListener('focusin', () => {
      hovering = true;
      clearTimeout(timer);
    });
    show.addEventListener('focusout', (event) => {
      if (show.contains(event.relatedTarget)) return;
      hovering = false;
      tick();
    });
    document.addEventListener('visibilitychange', tick);

    tick();
  });
})();
